'use client';

import { useState } from 'react';


import { NaumuFaceView, type FaceState } from './naumu-face-view';
import { Wordmark } from './wordmark';
import { UiLink } from '../lib/ui-link';

import { cn } from '../utils/cn';

interface MarketingLogoProps {
	/** When set, the lockup renders as a home link (header); otherwise a plain brand block (footers). */
	href?: string;
	className?: string;
}

/**
 * MarketingLogo - the marketing brand lockup (animated face + wordmark), shared
 * by the header and both footers.
 *
 * The face rests in `static` (the same pose `LogoFace variant="idle"` draws) and
 * winks once on hover. The engine drives `wink` to its own end and reports `idle`
 * when it lands, which is our cue to put it back to `static` - so it never settles
 * into idle's breathing/blinking loop.
 */
export const MarketingLogo = ({ href, className }: MarketingLogoProps) => {
	const [face, setFace] = useState<FaceState>('static');

	const wink = () => setFace('wink');
	const classes = cn('flex items-center gap-1.5 text-white', className);
	const lockup = (
		<>
			<NaumuFaceView
				state={face}
				onStateChange={(next) => {
					if (next === 'idle') setFace('static');
				}}
				className="size-[18px]"
			/>
			<Wordmark className="text-[16px]" />
		</>
	);

	if (href) {
		return (
			<UiLink href={href} aria-label="Naumu home" onMouseEnter={wink} className={classes}>
				{lockup}
			</UiLink>
		);
	}
	return (
		<div onMouseEnter={wink} className={classes}>
			{lockup}
		</div>
	);
};
